import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/App";
import { 
  ArrowLeft, Save, Car, User, Phone, Mail, Search, Clock, 
  Calendar, Package, PackageCheck, CheckCircle, X, ChevronLeft, ChevronRight, Camera, ImagePlus, Trash2, Send, FolderLock
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { format, addWeeks, addDays, startOfWeek, getWeek, isSameDay, subWeeks } from "date-fns";
import { fr } from "date-fns/locale";

export default function VehicleForm({ edit = false }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchingPlate, setSearchingPlate] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [availability, setAvailability] = useState([]);
  const [chargeData, setChargeData] = useState({});
  const [calendarWeek, setCalendarWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  
  // Auto-complétion
  const [clientSuggestions, setClientSuggestions] = useState([]);
  const [vehiculeSuggestions, setVehiculeSuggestions] = useState([]);
  const [showClientSuggestions, setShowClientSuggestions] = useState(false);
  const [showVehiculeSuggestions, setShowVehiculeSuggestions] = useState(false);
  const clientInputRef = useRef(null);
  const vehiculeInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  // Photos pour le RDV
  const [photos, setPhotos] = useState([]);
  
  // Note interne + SMS confirmation
  const [internalNote, setInternalNote] = useState("");
  const [smsDialog, setSmsDialog] = useState(false);
  const [createdVehicleId, setCreatedVehicleId] = useState(null);
  const [sendingSms, setSendingSms] = useState(false);
  
  const [formData, setFormData] = useState({
    immatriculation: "",
    marque: "",
    modele: "",
    couleur: "",
    energie: "",
    client: { nom: "", prenom: "", telephone: "", email: "" },
    type_intervention: "",
    sub_prestation: "",
    description: "",
    date_rdv: format(new Date(), "yyyy-MM-dd"),
    heure_rdv: "08:00",
    duree_estimee_heures: 2,
    duree_estimee_jours: 1,
    pieces_commandees: false
  });
  
  // Unité de durée: "heures" ou "jours"
  const [dureeUnite, setDureeUnite] = useState("heures");
  
  // Sous-prestations de la prestation sélectionnée
  const selectedPrestation = prestations.find(p => p.name === formData.type_intervention);
  const subPrestations = selectedPrestation?.sub_prestations || [];

  useEffect(() => {
    fetchPrestations();
    if (edit && id) fetchVehicle();
  }, [edit, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchAvailability();
    fetchChargeData();
  }, [formData.duree_estimee_heures, formData.duree_estimee_jours, dureeUnite, calendarWeek]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recherche clients
  const searchClients = async (query) => {
    console.log("searchClients called with:", query);
    if (query.length < 2) {
      setClientSuggestions([]);
      setShowClientSuggestions(false);
      return;
    }
    try {
      console.log("Making API call to /clients with search:", query);
      // Filtrer côté backend pour éviter de charger tous les clients
      const response = await api.get(`/clients?search=${encodeURIComponent(query)}&limit=8`);
      console.log("API response received, data length:", response.data?.length);
      const suggestions = response.data || [];
      console.log("Clients found:", suggestions.length, suggestions);
      setClientSuggestions(suggestions);
      if (suggestions.length > 0) {
        setShowClientSuggestions(true);
      }
    } catch (error) {
      console.error("Error searching clients:", error);
    }
  };

  // Recherche véhicules (immatriculation)
  const searchVehicules = async (query) => {
    if (query.length < 2) {
      setVehiculeSuggestions([]);
      return;
    }
    try {
      const response = await api.get(`/vehicules-db?search=${query}&limit=8`);
      setVehiculeSuggestions(response.data.vehicules || []);
      setShowVehiculeSuggestions((response.data.vehicules || []).length > 0);
    } catch (error) {
      console.error(error);
    }
  };

  // Sélection d'un client suggéré
  const selectClient = (client) => {
    setFormData(prev => ({
      ...prev,
      client: {
        nom: client.nom,
        prenom: client.prenom || "",
        telephone: client.telephone || "",
        email: client.email || ""
      }
    }));
    setShowClientSuggestions(false);
    setClientSuggestions([]);
    
    // Charger l'historique des véhicules de ce client
    fetchClientVehicles(client.id);
  };
  
  // Historique des véhicules du client sélectionné
  const [clientVehicles, setClientVehicles] = useState([]);
  
  const fetchClientVehicles = async (clientId) => {
    try {
      const response = await api.get(`/clients/${clientId}/vehicles`);
      setClientVehicles(response.data || []);
    } catch (error) {
      console.error(error);
      setClientVehicles([]);
    }
  };

  // Sélection d'un véhicule suggéré
  const selectVehicule = (vehicule) => {
    setFormData(prev => ({
      ...prev,
      immatriculation: vehicule.immatriculation || "",
      marque: vehicule.marque || "",
      modele: vehicule.modele || "",
      couleur: vehicule.couleur || "",
      energie: vehicule.energie || "",
      client: vehicule.client_nom ? {
        ...prev.client,
        nom: vehicule.client_nom.split(' ')[0] || vehicule.client_nom,
        prenom: vehicule.client_nom.split(' ').slice(1).join(' ') || ""
      } : prev.client
    }));
    setShowVehiculeSuggestions(false);
    setVehiculeSuggestions([]);
  };

  const fetchPrestations = async () => {
    try {
      const response = await api.get("/prestations");
      setPrestations(response.data);
      if (!edit && response.data.length > 0 && !formData.type_intervention) {
        setFormData(prev => ({ ...prev, type_intervention: response.data[0].name }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchVehicle = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/vehicles/${id}`);
      const v = response.data;
      
      // Déterminer l'unité de durée basée sur les valeurs stockées
      const heures = v.duree_estimee_heures || 2;
      const jours = v.duree_estimee_jours || 1;
      // Si heures = jours * 8, c'est probablement une entrée en jours
      if (jours > 1 || heures === jours * 8) {
        setDureeUnite("jours");
      } else {
        setDureeUnite("heures");
      }
      
      setFormData({
        immatriculation: v.immatriculation || "",
        marque: v.marque || "",
        modele: v.modele || "",
        couleur: v.couleur || "",
        energie: v.energie || "",
        client: v.client || { nom: "", prenom: "", telephone: "", email: "" },
        type_intervention: v.type_intervention || "",
        sub_prestation: v.sub_prestation || "",
        description: v.description || "",
        date_rdv: v.date_rdv?.split("T")[0] || format(new Date(), "yyyy-MM-dd"),
        heure_rdv: v.heure_rdv || "08:00",
        duree_estimee_heures: heures,
        duree_estimee_jours: jours,
        pieces_commandees: v.pieces_commandees || false
      });
    } catch (error) {
      toast.error("Véhicule non trouvé");
      navigate("/dashboard");
    }
    setLoading(false);
  };

  const fetchAvailability = async () => {
    try {
      // Calculer la durée en jours pour l'API de disponibilité
      let dureeJours;
      if (dureeUnite === "jours") {
        dureeJours = formData.duree_estimee_jours || 1;
      } else {
        dureeJours = Math.ceil(formData.duree_estimee_heures / 8) || 1;
      }
      const response = await api.get(`/planning/availability?duree_jours=${dureeJours}`);
      setAvailability(response.data.available_dates || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchChargeData = async () => {
    try {
      const startDate = format(calendarWeek, "yyyy-MM-dd");
      const endDate = format(addDays(addWeeks(calendarWeek, 2), -1), "yyyy-MM-dd");
      const response = await api.get(`/planning/charge?start_date=${startDate}&end_date=${endDate}`);
      setChargeData(response.data || {});
    } catch (error) {
      console.error(error);
    }
  };

  // Générer les jours du calendrier (2 semaines, lundi-vendredi)
  const getCalendarDays = () => {
    const days = [];
    for (let w = 0; w < 2; w++) {
      for (let d = 0; d < 5; d++) {
        days.push(addDays(addWeeks(calendarWeek, w), d));
      }
    }
    return days;
  };

  const searchPlate = async () => {
    if (!formData.immatriculation) return;
    setSearchingPlate(true);
    // Simulation - À remplacer par API SIV
    toast.info("Recherche plaque... (API SIV non configurée)");
    setSearchingPlate(false);
  };

  const handleChange = (field, value) => {
    console.log("handleChange called:", field, value);
    if (field.startsWith("client.")) {
      const clientField = field.replace("client.", "");
      setFormData(prev => ({
        ...prev,
        client: { ...prev.client, [clientField]: value }
      }));
      
      // Auto-complétion pour le nom client
      if (clientField === "nom") {
        console.log("Searching clients for:", value);
        searchClients(value);
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
      
      // Auto-complétion pour l'immatriculation
      if (field === "immatriculation") {
        searchVehicules(value);
      }
      
      // Auto-sélection Heures/Jours selon le type de prestation
      if (field === "type_intervention") {
        const prestation = prestations.find(p => p.name === value);
        if (prestation) {
          // Si la prestation a un workflow (carrosserie), suggérer "jours"
          // Sinon (mécanique), suggérer "heures"
          if (prestation.has_workflow) {
            setDureeUnite("jours");
            setFormData(prev => ({ ...prev, duree_estimee_jours: 1 }));
          } else {
            setDureeUnite("heures");
            setFormData(prev => ({ ...prev, duree_estimee_heures: 2 }));
          }
        }
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.immatriculation || !formData.marque || !formData.modele) {
      toast.error("Remplissez les infos véhicule");
      return;
    }
    if (!formData.client.nom || !formData.client.telephone) {
      toast.error("Remplissez nom et téléphone client");
      return;
    }
    if (!formData.type_intervention) {
      toast.error("Sélectionnez une prestation");
      return;
    }

    // Calculer les durées selon l'unité choisie
    let dataToSend;
    if (dureeUnite === "jours") {
      // En jours: convertir en heures (8h/jour) pour cohérence
      dataToSend = {
        ...formData,
        duree_estimee_jours: formData.duree_estimee_jours || 1,
        duree_estimee_heures: (formData.duree_estimee_jours || 1) * 8,
        photos: photos.map(p => p.data)  // Envoyer les photos
      };
    } else {
      // En heures: calculer les jours à partir des heures
      dataToSend = {
        ...formData,
        duree_estimee_heures: formData.duree_estimee_heures || 2,
        duree_estimee_jours: Math.ceil(formData.duree_estimee_heures / 8) || 1,
        photos: photos.map(p => p.data)  // Envoyer les photos
      };
    }

    setSaving(true);
    try {
      let vehicleId;
      if (edit) {
        const oldVehicle = await api.get(`/vehicles/${id}`);
        const oldDate = oldVehicle.data.date_rdv;
        await api.put(`/vehicles/${id}`, dataToSend);
        vehicleId = id;
        toast.success("Véhicule modifié");
        
        // Si la date a changé, proposer d'envoyer un SMS
        if (dataToSend.date_rdv && dataToSend.date_rdv !== oldDate) {
          setCreatedVehicleId(id);
          setSmsDialog("date_change");
        } else {
          navigate("/dashboard");
        }
      } else {
        const res = await api.post("/vehicles", dataToSend);
        vehicleId = res.data.id;
        toast.success("RDV créé");
        
        // Sauvegarder la note interne si renseignée
        if (internalNote.trim() && vehicleId) {
          try {
            await api.post(`/vehicles/${vehicleId}/internal-notes`, {
              vehicle_id: vehicleId,
              content: internalNote
            });
          } catch (e) {
            console.error("Échec sauvegarde de la note interne:", e);
          }
        }
        
        // Proposer l'envoi du SMS de confirmation
        setCreatedVehicleId(vehicleId);
        setSmsDialog("confirmation");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = "Erreur lors de la création";
      
      if (Array.isArray(detail)) {
        // Erreur de validation Pydantic - extraire les messages
        errorMessage = detail.map(d => String(d.msg || d.message || "Erreur")).join(", ");
      } else if (detail && typeof detail === 'object') {
        errorMessage = String(detail.msg || detail.message || JSON.stringify(detail));
      } else if (detail) {
        errorMessage = String(detail);
      }
      
      toast.error(errorMessage);
    }
    setSaving(false);
  };

  // Gestion des photos
  const openCamera = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handlePhotoCapture = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Compression via canvas
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 800;
          let width = img.width;
          let height = img.height;
          
          if (width > maxSize || height > maxSize) {
            if (width > height) {
              height = (height / width) * maxSize;
              width = maxSize;
            } else {
              width = (width / height) * maxSize;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);
          setPhotos(prev => [...prev, { id: Date.now() + Math.random(), data: compressedBase64 }]);
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
    // Reset input pour permettre de reprendre la même photo
    e.target.value = '';
  };

  const removePhoto = (photoId) => {
    setPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const getColorClass = (color) => {
    const colors = {
      slate: "bg-slate-100 text-slate-700 border-slate-300",
      amber: "bg-amber-100 text-amber-700 border-amber-300",
      orange: "bg-orange-100 text-orange-700 border-orange-300",
      blue: "bg-blue-100 text-blue-700 border-blue-300",
      purple: "bg-purple-100 text-purple-700 border-purple-300",
      emerald: "bg-emerald-100 text-emerald-700 border-emerald-300",
      red: "bg-red-100 text-red-700 border-red-300",
    };
    return colors[color] || colors.slate;
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in" data-testid="vehicle-form">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-black text-slate-900">
            {edit ? "Modifier le véhicule" : "Nouveau RDV"}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Type de prestation */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base">Type de prestation</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {prestations.map((prest) => (
                    <button
                      key={prest.id}
                      type="button"
                      onClick={() => handleChange("type_intervention", prest.name)}
                      className={`px-4 py-2 rounded-full border-2 font-medium transition-all ${
                        formData.type_intervention === prest.name
                          ? getColorClass(prest.color) + " ring-2 ring-offset-2 ring-slate-300"
                          : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      {prest.label}
                    </button>
                  ))}
                </div>
                
                {/* Sous-prestations */}
                {subPrestations.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <Label className="text-sm text-slate-600 mb-2 block">Sous-prestation (optionnel)</Label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => handleChange("sub_prestation", "")}
                        className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                          !formData.sub_prestation
                            ? "bg-slate-100 border-slate-300 text-slate-700"
                            : "bg-white border-slate-200 text-slate-500 hover:border-slate-300"
                        }`}
                      >
                        Aucune
                      </button>
                      {subPrestations.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          data-testid={`sub-prestation-chip-${sub.id}`}
                          onClick={() => {
                            handleChange("sub_prestation", sub.label);
                            // Mettre à jour la durée si définie
                            if (sub.duree_heures) {
                              setDureeUnite("heures");
                              handleChange("duree_estimee_heures", sub.duree_heures);
                            }
                          }}
                          className={`px-3 py-1.5 rounded-lg border text-sm font-medium transition-all ${
                            formData.sub_prestation === sub.label
                              ? "bg-red-50 border-red-300 text-red-700"
                              : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"
                          }`}
                        >
                          {sub.label}
                          {sub.duree_heures && (
                            <span className="ml-1 text-xs text-slate-400">({sub.duree_heures}h)</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Véhicule */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Car className="h-4 w-4" /> Véhicule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2 relative">
                  <div className="flex-1 relative">
                    <Label className="text-sm">Immatriculation *</Label>
                    <Input
                      ref={vehiculeInputRef}
                      placeholder="AA-123-BB"
                      value={formData.immatriculation}
                      onChange={(e) => handleChange("immatriculation", e.target.value.toUpperCase())}
                      onFocus={() => formData.immatriculation.length >= 2 && setShowVehiculeSuggestions(vehiculeSuggestions.length > 0)}
                      onBlur={() => setTimeout(() => setShowVehiculeSuggestions(false), 200)}
                      className="font-mono uppercase"
                      autoComplete="off"
                    />
                    {/* Suggestions véhicules */}
                    {showVehiculeSuggestions && vehiculeSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {vehiculeSuggestions.map((v) => (
                          <div
                            key={v.id}
                            onClick={() => selectVehicule(v)}
                            className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-mono font-bold text-slate-900">{v.immatriculation}</span>
                              <span className="text-xs text-slate-500">{v.client_nom}</span>
                            </div>
                            <p className="text-sm text-slate-600">{v.marque} {v.modele}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-end">
                    <Button type="button" variant="outline" onClick={searchPlate} disabled={searchingPlate}>
                      {searchingPlate ? <div className="spinner w-4 h-4" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Marque *</Label>
                    <Input value={formData.marque} onChange={(e) => handleChange("marque", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm">Modèle *</Label>
                    <Input value={formData.modele} onChange={(e) => handleChange("modele", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm">Couleur</Label>
                    <Input value={formData.couleur} onChange={(e) => handleChange("couleur", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-sm">Energie</Label>
                    <Input value={formData.energie} onChange={(e) => handleChange("energie", e.target.value)} />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Client */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="h-4 w-4" /> Client
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <Label className="text-sm">Nom *</Label>
                  <Input 
                    ref={clientInputRef}
                    value={formData.client.nom} 
                    onChange={(e) => handleChange("client.nom", e.target.value)}
                    autoComplete="off"
                  />
                  {/* Suggestions clients */}
                  {showClientSuggestions && clientSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                      {clientSuggestions.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => selectClient(c)}
                          onMouseDown={(e) => e.preventDefault()}
                          className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0"
                        >
                          <div className="font-medium text-slate-900">{c.prenom} {c.nom}</div>
                          <div className="text-sm text-slate-500">{c.telephone}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label className="text-sm">Prénom</Label>
                  <Input value={formData.client.prenom} onChange={(e) => handleChange("client.prenom", e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm">Téléphone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={formData.client.telephone} onChange={(e) => handleChange("client.telephone", e.target.value)} className="pl-10" />
                  </div>
                </div>
                <div>
                  <Label className="text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input value={formData.client.email} onChange={(e) => handleChange("client.email", e.target.value)} className="pl-10" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Historique véhicules du client */}
            {clientVehicles.length > 0 && (
              <Card className="border-emerald-200 bg-emerald-50/50">
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2 text-emerald-700">
                    <Car className="h-4 w-4" /> Véhicules de ce client ({clientVehicles.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {clientVehicles.slice(0, 5).map((v) => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => {
                        // Mise à jour en une seule fois de tous les champs véhicule
                        setFormData(prev => ({
                          ...prev,
                          immatriculation: v.immatriculation || "",
                          marque: v.marque || "",
                          modele: v.modele || "",
                          couleur: v.couleur || "",
                          energie: v.energie || "",
                          date_mise_circulation: v.date_mise_circulation || ""
                        }));
                        // Fermer les suggestions si ouvertes
                        setShowVehiculeSuggestions(false);
                        setVehiculeSuggestions([]);
                        toast.success(`Véhicule ${v.immatriculation} sélectionné`);
                      }}
                      className="w-full flex items-center justify-between p-2 rounded-lg border border-emerald-200 bg-white hover:bg-emerald-50 transition-colors text-left"
                    >
                      <div>
                        <span className="font-mono font-bold text-slate-900">{v.immatriculation}</span>
                        <span className="ml-2 text-sm text-slate-600">{v.marque} {v.modele}</span>
                      </div>
                      <CheckCircle className="h-4 w-4 text-emerald-500 opacity-0 group-hover:opacity-100" />
                    </button>
                  ))}
                  {clientVehicles.length > 5 && (
                    <p className="text-xs text-emerald-600 text-center">+ {clientVehicles.length - 5} autres véhicules</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Planning */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Planning & Durée
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm">Date RDV *</Label>
                    <Input
                      type="date"
                      value={formData.date_rdv}
                      onChange={(e) => handleChange("date_rdv", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-sm">Heure RDV *</Label>
                    <select
                      value={formData.heure_rdv}
                      onChange={(e) => handleChange("heure_rdv", e.target.value)}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base font-semibold"
                    >
                      {["07:00", "07:30", "08:00", "08:30", "09:00", "09:30", "10:00", "10:30", 
                        "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00", "14:30",
                        "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00"].map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                    <Label className="text-sm">Durée estimée</Label>
                    {/* Sélecteur d'unité */}
                    <div className="flex gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => setDureeUnite("heures")}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          dureeUnite === "heures"
                            ? "bg-red-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid="duree-heures-btn"
                      >
                        Heures
                      </button>
                      <button
                        type="button"
                        onClick={() => setDureeUnite("jours")}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                          dureeUnite === "jours"
                            ? "bg-red-600 text-white"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                        data-testid="duree-jours-btn"
                      >
                        Jours
                      </button>
                    </div>
                    {/* Champ de saisie selon l'unité */}
                    {dureeUnite === "heures" ? (
                      <>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="0.25"
                            step="0.25"
                            value={formData.duree_estimee_heures}
                            onChange={(e) => handleChange("duree_estimee_heures", parseFloat(e.target.value) || 0.25)}
                            className="flex-1"
                            data-testid="duree-heures-input"
                          />
                          {/* Boutons rapides pour mobile */}
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 px-2 text-xs"
                              onClick={() => handleChange("duree_estimee_heures", Math.max(0.25, (formData.duree_estimee_heures || 1) - 0.25))}
                            >
                              -15m
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-9 px-2 text-xs"
                              onClick={() => handleChange("duree_estimee_heures", (formData.duree_estimee_heures || 1) + 0.25)}
                            >
                              +15m
                            </Button>
                          </div>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.duree_estimee_heures < 1 
                            ? `≈ ${formData.duree_estimee_heures * 60} minutes` 
                            : formData.duree_estimee_heures < 8 
                            ? `≈ ${formData.duree_estimee_heures}h de travail` 
                            : `≈ ${Math.ceil(formData.duree_estimee_heures / 8)} jour(s) de travail`}
                        </p>
                      </>
                    ) : (
                      <>
                        <Input
                          type="number"
                          min="1"
                          step="1"
                          value={formData.duree_estimee_jours}
                          onChange={(e) => handleChange("duree_estimee_jours", parseInt(e.target.value) || 1)}
                          data-testid="duree-jours-input"
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {formData.duree_estimee_jours} jour(s) = {formData.duree_estimee_jours * 8}h de travail
                        </p>
                      </>
                    )}
                  </div>

                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    {formData.pieces_commandees ? (
                      <PackageCheck className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <Package className="h-5 w-5 text-red-500" />
                    )}
                    <Label className="font-medium">Pièces commandées</Label>
                  </div>
                  <Switch
                    checked={formData.pieces_commandees}
                    onCheckedChange={(v) => handleChange("pieces_commandees", v)}
                  />
                </div>
                
                <div>
                  <Label className="text-sm">Description des travaux</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    rows={3}
                  />
                </div>
                
                {/* Note interne (admin only) */}
                {!edit && (
                  <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <Label className="text-sm flex items-center gap-2 text-indigo-800">
                      <FolderLock className="h-4 w-4" />
                      Note interne (invisible pour les collaborateurs)
                    </Label>
                    <Textarea
                      value={internalNote}
                      onChange={(e) => setInternalNote(e.target.value)}
                      placeholder="Note interne pour le dossier..."
                      rows={2}
                      className="mt-1 bg-white"
                      data-testid="internal-note-input"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Photos du véhicule */}
            <Card className="border-slate-200">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="h-4 w-4" /> Photos du véhicule
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Input caméra caché */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handlePhotoCapture}
                  className="hidden"
                />
                
                {/* Boutons de capture */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                    onClick={openCamera}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Prendre photo
                  </Button>
                  <label className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotoCapture}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      asChild
                    >
                      <span>
                        <ImagePlus className="h-4 w-4 mr-2" />
                        Galerie
                      </span>
                    </Button>
                  </label>
                </div>
                
                {/* Grille de photos */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 gap-2">
                    {photos.map((photo) => (
                      <div key={photo.id} className="relative group">
                        <img 
                          src={photo.data} 
                          alt="Véhicule" 
                          className="w-full h-20 object-cover rounded-lg border border-slate-200"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(photo.id)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {photos.length === 0 && (
                  <p className="text-center text-sm text-slate-500 py-4">
                    Aucune photo ajoutée
                  </p>
                )}
                
                {photos.length > 0 && (
                  <p className="text-xs text-slate-500 text-center">
                    {photos.length} photo(s) - Les photos seront enregistrées avec le dossier
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                Annuler
              </Button>
              <Button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700 text-white">
                {saving ? <div className="spinner" /> : (
                  <><Save className="mr-2 h-4 w-4" />{edit ? "Enregistrer" : "Créer"}</>
                )}
              </Button>
            </div>
          </form>
        </div>

        {/* Sidebar - Mini Planning */}
        <div>
          <Card className="border-slate-200 sticky top-6">
            <CardHeader className="py-2 px-3">
              <CardTitle className="text-sm flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Planning
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setCalendarWeek(subWeeks(calendarWeek, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm font-bold text-slate-700 min-w-[35px] text-center">
                    S{getWeek(calendarWeek, { weekStartsOn: 1 })}
                  </span>
                  <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                    onClick={() => setCalendarWeek(addWeeks(calendarWeek, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0 px-2 pb-2">
              {/* Suggestion créneau */}
              {(() => {
                const isMeca = formData.type_intervention === "mecanique";
                const dureeJ = dureeUnite === "jours" ? (formData.duree_estimee_jours || 1) : Math.max(1, Math.ceil((formData.duree_estimee_heures || 2) / 8));
                const today = new Date(); today.setHours(0,0,0,0);
                let bestDate = null;
                let bestCount = 999;
                for (let w = 0; w < 2; w++) {
                  for (let d = 0; d < 5; d++) {
                    const day = addDays(calendarWeek, w * 7 + d);
                    if (day < today) continue;
                    const dateStr = format(day, "yyyy-MM-dd");
                    const dc = chargeData[dateStr];
                    if (!dc) continue;
                    const count = isMeca ? (dc.meca_vehicles?.length || 0) : (dc.carro_vehicles?.length || 0);
                    if (dureeJ > 1) {
                      let maxC = count; let ok = true;
                      for (let dd = 1; dd < dureeJ; dd++) {
                        const ns = format(addDays(day, dd), "yyyy-MM-dd");
                        const nd = chargeData[ns];
                        if (!nd) { ok = false; break; }
                        const nc = isMeca ? (nd.meca_vehicles?.length || 0) : (nd.carro_vehicles?.length || 0);
                        if (nc > maxC) maxC = nc;
                      }
                      if (ok && maxC < bestCount) { bestCount = maxC; bestDate = dateStr; }
                    } else {
                      if (count < bestCount) { bestCount = count; bestDate = dateStr; }
                    }
                  }
                }
                if (bestDate && formData.type_intervention && bestDate !== formData.date_rdv) {
                  return (
                    <button type="button" onClick={() => handleChange("date_rdv", bestDate)}
                      className="w-full mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-left hover:bg-blue-100 transition-all">
                      <p className="text-[10px] font-bold text-blue-700">Créneau suggéré</p>
                      <p className="text-xs text-blue-600">
                        {format(new Date(bestDate), "EEEE d MMMM", { locale: fr })} — {bestCount} {isMeca ? "méca" : "carro"} ce jour
                      </p>
                    </button>
                  );
                }
                return null;
              })()}

              {[0, 1].map((weekIdx) => {
                const weekOffset = weekIdx * 7;
                return (
                  <div key={weekIdx} className={weekIdx > 0 ? "mt-2 pt-2 border-t border-dashed border-slate-300" : ""}>
                    {/* En-têtes jours */}
                    <div className="grid grid-cols-5 gap-1 mb-1">
                      {[0,1,2,3,4].map((d) => {
                        const day = addDays(calendarWeek, weekOffset + d);
                        const dateStr = format(day, "yyyy-MM-dd");
                        const isSelected = formData.date_rdv === dateStr;
                        const isToday = isSameDay(day, new Date());
                        return (
                          <button key={d} type="button"
                            onClick={() => handleChange("date_rdv", dateStr)}
                            className={`text-center py-0.5 rounded-t text-[10px] font-bold transition-all ${
                              isSelected ? "bg-red-600 text-white" :
                              isToday ? "bg-red-100 text-red-700" :
                              "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}>
                            <div>{format(day, "EEE", { locale: fr })}</div>
                            <div className="text-xs">{format(day, "d")}</div>
                          </button>
                        );
                      })}
                    </div>

                    {/* MÉCANIQUE */}
                    <div className="mb-1">
                      <div className="text-[9px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded-t border-b border-emerald-200">MÉCANIQUE</div>
                      <div className="grid grid-cols-5 gap-px bg-emerald-50/30 rounded-b min-h-[45px]">
                        {[0,1,2,3,4].map((d) => {
                          const day = addDays(calendarWeek, weekOffset + d);
                          const dateStr = format(day, "yyyy-MM-dd");
                          const dc = chargeData[dateStr] || { meca_vehicles: [] };
                          const isSelected = formData.date_rdv === dateStr;
                          return (
                            <div key={d} onClick={() => handleChange("date_rdv", dateStr)}
                              className={`cursor-pointer p-0.5 space-y-px border-r last:border-r-0 border-emerald-100 ${
                                isSelected ? "bg-red-50 ring-1 ring-red-400 rounded" : "hover:bg-emerald-50"
                              }`}>
                              {dc.meca_vehicles?.length === 0 ? (
                                <div className="h-full flex items-center justify-center"><span className="text-[8px] text-slate-300">—</span></div>
                              ) : dc.meca_vehicles?.map((v, vi) => (
                                <div key={vi} className="bg-emerald-500 text-white text-[7px] px-0.5 py-px rounded leading-tight" title={v.immatriculation + (v.description ? ' - ' + v.description : '')}>
                                  <div className="font-bold truncate">{v.client_nom || v.immatriculation}</div>
                                  {v.description && <div className="truncate opacity-80 text-[6px]">{v.description}</div>}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* CARROSSERIE */}
                    <div>
                      <div className="text-[9px] font-bold text-orange-700 bg-orange-50 px-1 py-0.5 rounded-t border-b border-orange-200">CARROSSERIE</div>
                      <div className="grid grid-cols-5 gap-px bg-orange-50/30 rounded-b min-h-[45px]">
                        {[0,1,2,3,4].map((d) => {
                          const day = addDays(calendarWeek, weekOffset + d);
                          const dateStr = format(day, "yyyy-MM-dd");
                          const dc = chargeData[dateStr] || { carro_vehicles: [] };
                          const isSelected = formData.date_rdv === dateStr;
                          return (
                            <div key={d} onClick={() => handleChange("date_rdv", dateStr)}
                              className={`cursor-pointer p-0.5 space-y-px border-r last:border-r-0 border-orange-100 ${
                                isSelected ? "bg-red-50 ring-1 ring-red-400 rounded" : "hover:bg-orange-50"
                              }`}>
                              {dc.carro_vehicles?.length === 0 ? (
                                <div className="h-full flex items-center justify-center"><span className="text-[8px] text-slate-300">—</span></div>
                              ) : dc.carro_vehicles?.map((v, vi) => (
                                <div key={vi} className="bg-orange-500 text-white text-[7px] px-0.5 py-px rounded leading-tight" title={v.immatriculation + (v.description ? ' - ' + v.description : '')}>
                                  <div className="font-bold truncate">{v.client_nom || v.immatriculation}</div>
                                  {v.description && <div className="truncate opacity-80 text-[6px]">{v.description}</div>}
                                </div>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* SMS Confirmation Dialog */}
      <AlertDialog open={!!smsDialog} onOpenChange={(open) => { if (!open) { setSmsDialog(false); navigate("/dashboard"); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-blue-600" />
              {smsDialog === "confirmation" ? "Envoyer un SMS de confirmation ?" : "Envoyer un SMS de modification ?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {smsDialog === "confirmation"
                ? `Voulez-vous envoyer un SMS de confirmation au client pour ce nouveau RDV ?`
                : `La date du RDV a été modifiée. Voulez-vous envoyer un SMS au client pour l'informer ?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingSms} onClick={() => { setSmsDialog(false); navigate("/dashboard"); }}>
              Non, pas maintenant
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={sendingSms}
              className="bg-blue-600 hover:bg-blue-700"
              onClick={async () => {
                setSendingSms(true);
                try {
                  const endpoint = smsDialog === "confirmation"
                    ? `/vehicles/${createdVehicleId}/send-confirmation-sms`
                    : `/vehicles/${createdVehicleId}/send-date-change-sms`;
                  await api.post(endpoint);
                  toast.success("SMS envoyé");
                } catch (e) {
                  toast.error("Erreur envoi SMS");
                }
                setSendingSms(false);
                setSmsDialog(false);
                navigate("/dashboard");
              }}
            >
              {sendingSms ? <div className="spinner" /> : "Oui, envoyer le SMS"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
